// Donor-scope pledges API helper — CHG-1513 portal-pivot retarget,
// CHG-2947 (Slice F, DP-R5) server-side paging + filters.
//
// Backend: portal.py canonical at GET /api/v1/my-giving/pledges.
// CHG-2947 made the route param-presence-switched: sending page/per_page
// returns the donor-portal FLAT paged envelope
// ({data, page, per_page, total, total_pages}) with server-side
// org_id/status filters; sending nothing keeps the legacy bare array
// (v1 api.ts + mobile unchanged). This helper ALWAYS pages on the server
// and normalizes through the CHG-2916 list-contract module (CHG-2947
// flat-envelope branch).
//
// Org-filter source: GET /my-giving/pledges-organizations (CHG-1220) is
// the CANONICAL org-chip source for the pledges screen (count-correct:
// only orgs the account has pledges in) — deliberately NOT folded into
// this list contract (DP-R5 resolution, tier-04 donor-portal.api.yaml).
//
// Not sent (no such params server-side — dropped no-ops): search,
// sortField, sortDirection, source CSV.

import { apiClient } from "./client";
import {
  listPageParams,
  normalizeListResponse,
  type NormalizedList,
  type RawListEnvelope,
} from "./list-contract";

export interface PortalPledgeRead {
  id: string;
  organization_id: string;
  org_name: string | null;
  org_logo_url: string | null;
  designation_id: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  session_id: string | null;
  session_name: string | null;
  total_amount_minor_units: number;
  currency: string;
  fulfilled_minor_units: number;
  outstanding_minor_units: number;
  frequency: string;
  start_date: string | null;
  due_date: string | null;
  status: string;
  designation_name: string | null;
  created_at: string;
}

export interface ListMyPledgesParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  /** Server-side org filter — CHG-2947. */
  org_id?: string;
  /** Server-side status filter — CHG-2947. */
  status?: string;
}

export async function listMyPledges(
  params: ListMyPledgesParams = {},
): Promise<NormalizedList<PortalPledgeRead>> {
  const { page, per_page } = listPageParams(params);
  const r = await apiClient().get<RawListEnvelope<PortalPledgeRead>>(
    "/my-giving/pledges",
    {
      params: {
        page,
        per_page,
        org_id: params.org_id,
        status: params.status,
      },
    },
  );
  return normalizeListResponse<PortalPledgeRead>(r.data);
}

export const mePledgesApi = {
  listMyPledges,
};
